import assertNever from "assert-never";
import React from "react";
import { Page } from "../activities/AppPage";
import { API } from "../API";
import { callNumber as callNumberDirectly } from "../external_link";
import useCaregiver from "../hooks/useCaregiver";
import useMixin from "../hooks/useMixin";
import { useUploadVerificationSelfieMutation } from "../hooks/useUploadVerificationSelfieMutation";
import { isLoading, Loading, loading } from "../Loading";
import { AgencyId, CaregiverPatientDocument, PatientId, VisitInstanceId } from "../messages";
import { ClockedInStatus, VisitInstanceDetails } from "../messages/Visit";
import { AppState } from "../system/AppState";
import { AgencyMember } from "../system/AppState/AppAgenciesState";
import { AppCaregiver } from "../system/AppState/AppCaregiverState";
import { AppChatState } from "../system/AppState/ChatState";
import { List } from "../utils/List";
import { AgenciesBlock, convertToAgenciesBlocks } from "./AgenciesList/AgenciesListUtils";
import ChangesSavedBanner from "./ChangesSavedBanner";
import ClockInOutModals from "./ClockInOutModals";
import { AppOverlay } from "./Generic/AppOverlay";
import { isDefined } from "./Generic/IsDefined";
import LoadingScaffold from "./LoadingScaffold";
import CallPatientBottomSheet from "./Patient/components/CallPatientBottomSheet";
import DirectionsBottomSheet from "./Patient/components/DirectionsBottomSheet";
import { useCallNumber } from "./Patient/useCallNumber";
import useCallPatient from "./useCallPatient";
import useContactSupport from "./useContactSupport";
import useVbpNursingQuestions from "./useVbpNursingQuestions";
import useVisitInstanceClockInOut from "./useVisitInstanceClockInOut";
import useVisitInstanceDirections from "./useVisitInstanceDirections";
import { InitialPlanOfCare } from "./useVisitInstancePlanOfCare";
import { useVisitChatIcon } from "./VisitChatIcon";
import { useVisitInstanceDocumentsAndPlanOfCareActivity } from "./VisitInstanceDocumentsAndPlanOfCareActivity";
import VisitInstanceScreen from "./VisitInstanceScreen";
import MedflytVisitLetsStartBottomSheet from "./VisitLetsStartBottomSheet/MedflytVisitLetsStartBottomSheet";
import VisitLetsStartBottomSheet from "./VisitLetsStartBottomSheet/VisitLetsStartBottomSheet";
import WithDutySheet from "./WithDutySheet";
import WithLetsStartBottomSheet from "./WithLetsStartBottomSheet";
import WithMedflytLetsStartBottomSheet from "./WithMedflytLetsStartBottomSheet";

interface Props {
  visitInstanceId: VisitInstanceId;
  api: API;
  appState: AppState;
  onClickChatWithAgency: (patientId: PatientId, visitInstanceId: VisitInstanceId) => void;
  onClickGoBack: () => void;
  onRequestNavigate: (page: Page) => void;
}

type State =
  | { type: "Loading" }
  | {
      type: "Ready";
      caregiver: AppCaregiver;
      coordinator: AgencyMember;
      visitInstance: VisitInstanceDetails;
      clockStatus: ClockedInStatus;
      countNewPatientChatMessages: number;
      activeAgencies: List<AgenciesBlock>;
    };

const useVisitInstanceActivity = (props: Props) => {
  const [state, setState] = React.useState<State>({ type: "Loading" });

  const mixin = useMixin(props.appState);
  const caregiver = useCaregiver(props.appState);

  const {
    state: documentsState,
    actions: documentsActions,
    _internals,
  } = useVisitInstanceDocumentsAndPlanOfCareActivity({
    api: props.api,
    appState: props.appState,
    caregiver: caregiver,
    visitInstanceId: props.visitInstanceId,
    onClickDocument: (document) => {
      if (state.type === "Ready") {
        props.onRequestNavigate({
          type: "ScheduledVisitDocScreen",
          requiredDocument: document,
          updateDocuments: _internals.initDocumentsState,
          patientId: state.visitInstance.publicVisitDetails.patientId,
        });
      }
    },
    onClickPlanOfCare: (planOfCare) => {
      props.onRequestNavigate({
        type: "VisitPlanOfCare",
        visitInstanceId: props.visitInstanceId,
        init: planOfCare,
        updatePlanOfCare: _internals.initPlanOfCareState,
      });
    },
  });

  React.useEffect(() => {
    if (
      isLoading(caregiver) ||
      isLoading(props.appState.clockedInStatus) ||
      isLoading(props.appState.appAgenciesState) ||
      isLoading(props.appState.scheduledVisits)
    ) {
      return;
    }

    const visitInstance = props.appState.scheduledVisits.scheduledVisitDetails.find(
      (x) => x.id === props.visitInstanceId
    );

    if (!isDefined(visitInstance)) {
      throw new Error(`Could not find visit instance ${props.visitInstanceId} in appstate`);
    }

    const coordinator = props.appState.appAgenciesState.allAgencyMembers.get(
      visitInstance.publicVisitDetails.createdById
    );

    if (!isDefined(coordinator)) {
      throw new Error(`
                Could not find coordinator ${visitInstance.publicVisitDetails.createdById}
                of visit instance ${props.visitInstanceId} in appstate
            `);
    }

    const countNewPatientChatMessages = AppChatState.patientNumNewChatMessages(
      mixin.appChatState,
      visitInstance.publicVisitDetails.patientId
    );

    const activeAgencies = convertToAgenciesBlocks(props.appState.appAgenciesState.agencies).filter(
      (x) => x.status === "ACTIVE"
    );

    setState({
      type: "Ready",
      caregiver: caregiver,
      clockStatus: props.appState.clockedInStatus,
      coordinator: coordinator,
      countNewPatientChatMessages: countNewPatientChatMessages,
      visitInstance: visitInstance,
      activeAgencies: activeAgencies,
    });
  }, [
    caregiver,
    documentsState.type,
    mixin.appChatState,
    props.appState.appAgenciesState,
    props.appState.clockedInStatus,
    props.appState.scheduledVisits,
    props.visitInstanceId,
  ]);

  const handleClickCall = (agencyId: AgencyId, phoneNumber: string | null) => {
    if (phoneNumber !== null) {
      callNumberDirectly(phoneNumber);
    }
    return props.api.recordCallToAgencyClickByVisitInstanceId(props.visitInstanceId, agencyId);
  };

  const handleClickOpenPatient = (patientId: PatientId) => {
    return props.onRequestNavigate({ type: "Patient", patientId: patientId });
  };

  return {
    caregiver: caregiver,
    state: state,
    actions: {
      handleClickCall: handleClickCall,
      handleClickOpenPatient: handleClickOpenPatient,
    },
    documentsState: documentsState,
    documentsActions: {
      ...documentsActions,
      openPlanOfCare: (planOfCare: InitialPlanOfCare) => {
        props.onRequestNavigate({
          type: "VisitPlanOfCare",
          init: planOfCare,
          visitInstanceId: props.visitInstanceId,
          updatePlanOfCare: _internals.initPlanOfCareState,
        });
      },
      openDocument: (document: CaregiverPatientDocument) => {
        if (state.type === "Ready") {
          props.onRequestNavigate({
            type: "ScheduledVisitDocScreen",
            requiredDocument: document,
            updateDocuments: _internals.initDocumentsState,
            patientId: state.visitInstance.publicVisitDetails.patientId,
          });
        }
      },
    },
  };
};

const VisitInstanceActivity = (props: Props) => {
  const { caregiver, state, actions, documentsActions, documentsState } =
    useVisitInstanceActivity(props);
  const {
    state: { isPopperOpen },
  } = useVisitChatIcon();

  const {
    vbpNursingQuestions,
    changeVbpNursingQuestion,
    answerVbpNursingQuestion,
    submitVbpNursingReport,
  } = useVbpNursingQuestions({ visitInstanceId: props.visitInstanceId, api: props.api });

  const handleRequireManualClock = () =>
    props.onRequestNavigate({
      type: "TimesheetActivity",
      visitInstanceId: props.visitInstanceId,
    });

  const handleUploadVerificationSelfie = useUploadVerificationSelfieMutation({
    api: props.api,
  });

  const {
    canClockIn,
    canClockOut,
    canSubmitTimesheet,
    promptClockInMapModal,
    promptClockOutMapModal,
    clockInOutModalsState,
  } = useVisitInstanceClockInOut({
    onRequireManualClock: handleRequireManualClock,
    isCaregiverOfMedflyt: state.type === "Loading" ? false : state.caregiver.isCaregiverOfMedFlyt,
    visitInstance: state.type === "Loading" ? loading() : state.visitInstance,
    caregiverId: state.type === "Loading" ? loading() : state.caregiver.id,
    api: props.api,
    appState: props.appState,
    onUploadVerificationSelfie: (visitInstanceId, imageBase64, clockType) =>
      handleUploadVerificationSelfie.mutateAsync({
        visitInstanceId,
        imageBase64,
        clockType,
      }),
  });

  const { openLocation, closeLocation, directionsState } = useVisitInstanceDirections({
    visitInstance: state.type === "Loading" ? loading() : state.visitInstance,
  });

  const { isCallPatientOpen, closeCallPatient, openCallPatient } = useCallPatient();

  const [activeVisit, setActiveVisit] = React.useState<VisitInstanceDetails | Loading>(loading());
  const [isVisitBottomSheetOpen, setVisitBottomSheetOpen] = React.useState(false);

  const contactSupport = useContactSupport({
    api: props.api,
    appState: props.appState,
    onRequestNavigate: props.onRequestNavigate,
    onClickNewChatWithMedflyt: () => {
      if (state.type === "Ready") {
        props.onClickChatWithAgency(
          state.visitInstance.publicVisitDetails.patientId,
          state.visitInstance.id
        );
      }
    },
  });

  const [isChangesSavedBannerOpen, setChangesSavedBannerOpen] = React.useState(false);

  const handleOpenChangesSavedBanner = () => {
    setChangesSavedBannerOpen(true);
  };

  const handleHideOrShowVisitBottomSheet = (isOpen: boolean) => {
    setVisitBottomSheetOpen(isOpen);
  };

  const handleCloseChangesSavedBanner = () => {
    setChangesSavedBannerOpen(false);
  };

  const handleOpenVisitBottomSheet = (visitInstance: VisitInstanceDetails) => {
    setActiveVisit(visitInstance);
    setVisitBottomSheetOpen(true);
  };

  const handleUpdateActiveVisit = (visitInstanceId: VisitInstanceId) => {
    if (isLoading(props.appState.scheduledVisits)) {
      return;
    }
    const visitInstance = props.appState.scheduledVisits.scheduledVisitDetails.find(
      (x) => x.id === visitInstanceId
    );
    if (visitInstance === undefined) {
      return;
    }
    handleOpenVisitBottomSheet(visitInstance);
  };

  const handleSubmitDutySheet = (visitInstanceId: VisitInstanceId) => {
    props.appState.setVisitInstanceDutySheetCompleted(visitInstanceId, true);
    if (!isLoading(activeVisit)) {
      setActiveVisit({ ...activeVisit, isDutySheetCompleted: true });
    }
  };

  const callNumber = useCallNumber({ api: props.api, caregiver: caregiver });

  const visitLetsStartBottomSheet = (() => {
    if (isLoading(activeVisit) || isLoading(caregiver)) {
      return null;
    }
    return (
      <WithDutySheet activeVisit={activeVisit} api={props.api}>
        {caregiver.isCaregiverOfMedFlyt
          ? (initialDutySheetData) => (
              <WithMedflytLetsStartBottomSheet
                activeEntity={{ type: "visit", visit: activeVisit }}
                api={props.api}
                appState={props.appState}
                initialDutySheetData={initialDutySheetData}
                isOpen={isVisitBottomSheetOpen}
                onHideOrShowVisitBottomSheet={handleHideOrShowVisitBottomSheet}
                onOpenChangesSavedBanner={handleOpenChangesSavedBanner}
                onUpdateActiveVisit={handleUpdateActiveVisit}
                onSubmitDutySheet={handleSubmitDutySheet}
              >
                {(bottomSheetProps) => (
                  <MedflytVisitLetsStartBottomSheet
                    {...bottomSheetProps}
                    contactSupport={contactSupport}
                  />
                )}
              </WithMedflytLetsStartBottomSheet>
            )
          : (initialDutySheetData) => (
              <WithLetsStartBottomSheet
                activeVisit={activeVisit}
                api={props.api}
                appState={props.appState}
                initialDutySheetData={initialDutySheetData}
                isOpen={isVisitBottomSheetOpen}
                onHideOrShowVisitBottomSheet={handleHideOrShowVisitBottomSheet}
                onOpenChangesSavedBanner={handleOpenChangesSavedBanner}
                onUpdateActiveVisit={handleUpdateActiveVisit}
                onSubmitDutySheet={handleSubmitDutySheet}
              >
                {(bottomSheetProps) => (
                  <VisitLetsStartBottomSheet
                    {...bottomSheetProps}
                    contactSupport={contactSupport}
                  />
                )}
              </WithLetsStartBottomSheet>
            )}
      </WithDutySheet>
    );
  })();

  switch (state.type) {
    case "Loading":
      return <LoadingScaffold onClickGoBack={props.onClickGoBack} />;
    case "Ready":
      return (
        <>
          <VisitInstanceScreen
            documentsState={documentsState}
            caregiver={state.caregiver}
            clockStatus={state.clockStatus}
            coordinator={state.coordinator}
            numNewChatMessages={state.countNewPatientChatMessages}
            vbpItemWithAnswers={vbpNursingQuestions}
            visitInstance={state.visitInstance}
            activeAgencies={state.activeAgencies}
            canClockIn={canClockIn()}
            canClockOut={canClockOut()}
            canSubmitTimesheet={canSubmitTimesheet()}
            isPopperOpen={isPopperOpen}
            onClickDeleteFreeScan={documentsActions.promptDeleteFreeScan}
            onUploadDocument={documentsActions.uploadNewDocument}
            onAnswerVBPQuestion={answerVbpNursingQuestion}
            onChangeVBPQuestion={changeVbpNursingQuestion}
            onSubmitVBPQuestions={submitVbpNursingReport}
            onClickCall={actions.handleClickCall}
            onClickChat={props.onClickChatWithAgency}
            onClickClockIn={promptClockInMapModal}
            onClickClockOut={promptClockOutMapModal}
            onClickSubmitTimesheet={handleRequireManualClock}
            onClickOpenPatient={actions.handleClickOpenPatient}
            onClickCallPatient={openCallPatient}
            onClickDirections={openLocation}
            onClickDocument={documentsActions.openDocument}
            onClickPlanOfCare={documentsActions.openPlanOfCare}
            onClickGoBack={props.onClickGoBack}
            onClickOpenBottomSheet={handleOpenVisitBottomSheet}
          />
          <CallPatientBottomSheet
            isOpen={isCallPatientOpen}
            homePhone={state.visitInstance.confidentialVisitDetails.patientPhone}
            mobilePhone={state.visitInstance.confidentialVisitDetails.patientAlternativePhone}
            onClickPhone={(number) => {
              return callNumber({
                agencyId: state.visitInstance.publicVisitDetails.agency.id,
                destination: number,
                name: state.visitInstance.confidentialVisitDetails.patientName,
              });
            }}
            onRequestClose={closeCallPatient}
          />
          <DirectionsBottomSheet {...directionsState} onRequestClose={closeLocation} />
          <ChangesSavedBanner
            isOpen={isChangesSavedBannerOpen}
            onClose={handleCloseChangesSavedBanner}
          />
          {visitLetsStartBottomSheet}

          {isLoading(contactSupport) ? null : (
            <AppOverlay>
              <ClockInOutModals {...clockInOutModalsState} contactSupportProps={contactSupport} />
            </AppOverlay>
          )}
        </>
      );
    default:
      assertNever(state);
  }
};

export default VisitInstanceActivity;
